import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminUpdateConsultantHoursTransaction,
  getAdminGetOrganizationQueryKey,
  getAdminGetConsultantHoursTransactionsQueryKey,
} from '@/api/auth/admin/admin';
import type { ConsultantHoursTransactionResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { logger } from '@/services/logger';
import {
  consultantHoursTransactionSchema,
  type ConsultantHoursTransactionForm,
} from '@/schemas/consultant-hours';
import { ConsultantHoursTransactionFields } from './ConsultantHoursTransactionFields';

interface EditConsultantHoursTransactionDialogProps {
  organizationId: string;
  /** The ledger entry being edited; the dialog is open while this is set. */
  transaction: ConsultantHoursTransactionResponse | null;
  onClose: () => void;
}

/**
 * Dialog for editing a consultant hours ledger entry. The organization's totals and every running
 * balance are recalculated by the server; Stripe purchases can only have their description edited.
 */
export function EditConsultantHoursTransactionDialog({
  organizationId,
  transaction,
  onClose,
}: EditConsultantHoursTransactionDialogProps) {
  const queryClient = useQueryClient();
  const isStripePurchase = !!transaction?.stripeCheckoutSessionId;

  const form = useForm<ConsultantHoursTransactionForm>({
    resolver: zodResolver(consultantHoursTransactionSchema),
  });
  const { handleSubmit, reset } = form;

  useEffect(() => {
    if (transaction) {
      reset({
        type: transaction.type,
        hours: transaction.hours,
        description: transaction.description ?? '',
      });
    }
  }, [transaction, reset]);

  const { mutate, isPending } = useAdminUpdateConsultantHoursTransaction({
    mutation: {
      onSuccess: () => {
        toast.success('Consultant hours entry updated');
        onClose();
        queryClient.invalidateQueries({
          queryKey: getAdminGetOrganizationQueryKey(organizationId),
        });
        queryClient.invalidateQueries({
          queryKey: getAdminGetConsultantHoursTransactionsQueryKey(organizationId),
        });
      },
      onError: (error: any) => {
        logger.error(error, {
          context: 'UpdateConsultantHoursTransaction',
          orgId: organizationId,
          transactionId: transaction?.id,
        });
        toast.error('Failed to update entry. ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  const onSubmit = useCallback(
    (data: ConsultantHoursTransactionForm) => {
      if (!transaction) {
        return;
      }

      // An empty description clears it; Stripe purchases only send the description.
      mutate({
        orgId: organizationId,
        transactionId: transaction.id,
        data: isStripePurchase
          ? { description: data.description.trim() }
          : { type: data.type, hours: data.hours, description: data.description.trim() },
      });
    },
    [isStripePurchase, mutate, organizationId, transaction]
  );

  return (
    <Dialog open={!!transaction} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Consultant Hours Entry</DialogTitle>
          <DialogDescription>
            The organization&apos;s totals and every later balance are recalculated. An edit that
            would take the available hours below zero at any point is rejected.
          </DialogDescription>
        </DialogHeader>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <ConsultantHoursTransactionFields lockAmount={isStripePurchase} />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
